import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell, Mail, Send, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

interface NotifSettings {
  email_enabled: boolean;
  telegram_enabled: boolean;
  telegram_bot_token: string;
  telegram_chat_id: string;
  min_duration_seconds: number;
  min_pages: number;
  quiet_hours_start: number | null;
  quiet_hours_end: number | null;
}

export function VisitorNotifSettings() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const [s, setS] = useState<NotifSettings>({
    email_enabled: true,
    telegram_enabled: false,
    telegram_bot_token: "",
    telegram_chat_id: "",
    min_duration_seconds: 30,
    min_pages: 1,
    quiet_hours_start: null,
    quiet_hours_end: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/visitors/notification-settings`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setS({ ...s, ...d });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND}/api/visitors/notification-settings`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(s),
      });
      if (!r.ok) throw new Error("save failed");
      toast.success("Préférences enregistrées");
    } catch {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await fetch(`${BACKEND}/api/visitors/notification-test`, {
        method: "POST",
        credentials: "include",
      });
      const d = await r.json();
      const msgs: string[] = [];
      if (d.email) msgs.push(`Email : ${d.email === "ok" ? "✓ envoyé" : d.email}`);
      if (d.telegram) msgs.push(`Telegram : ${d.telegram === "ok" ? "✓ envoyé" : d.telegram}`);
      toast.success(msgs.join(" · ") || "Test exécuté");
    } catch (e: any) {
      toast.error(e.message || "Erreur");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return null;

  return (
    <Card data-testid="visitor-notif-settings">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          Notifications visiteurs chauds
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Email */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Mail className="h-5 w-5 text-primary mt-1" />
            <div>
              <div className="font-medium">Email Gmail</div>
              <p className="text-xs text-muted-foreground">
                Notification envoyée à votre Gmail connecté quand un visiteur reste {s.min_duration_seconds}s+.
              </p>
            </div>
          </div>
          <Switch
            checked={s.email_enabled}
            onCheckedChange={(v) => setS({ ...s, email_enabled: v })}
            data-testid="notif-email-toggle"
          />
        </div>

        {/* Telegram */}
        <div className="border-t border-border/40 pt-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Send className="h-5 w-5 text-sky-400 mt-1" />
              <div>
                <div className="font-medium">Telegram (notif instantanée sur téléphone)</div>
                <p className="text-xs text-muted-foreground">
                  Le bot Telegram envoie une push immédiate sur votre iPhone.
                </p>
              </div>
            </div>
            <Switch
              checked={s.telegram_enabled}
              onCheckedChange={(v) => setS({ ...s, telegram_enabled: v })}
              data-testid="notif-telegram-toggle"
            />
          </div>

          {s.telegram_enabled && (
            <div className="mt-3 space-y-3 pl-8">
              <div>
                <Label htmlFor="tg-token">Bot Token (BotFather)</Label>
                <Input
                  id="tg-token"
                  type="password"
                  value={s.telegram_bot_token}
                  onChange={(e) => setS({ ...s, telegram_bot_token: e.target.value })}
                  placeholder="123456789:AAH..."
                  data-testid="notif-telegram-token-input"
                />
              </div>
              <div>
                <Label htmlFor="tg-chat">Chat ID</Label>
                <Input
                  id="tg-chat"
                  value={s.telegram_chat_id}
                  onChange={(e) => setS({ ...s, telegram_chat_id: e.target.value })}
                  placeholder="123456789"
                  data-testid="notif-telegram-chat-input"
                />
              </div>
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">📖 Comment obtenir token + chat ID ?</summary>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                  <li>
                    Sur Telegram, ouvrez{" "}
                    <a
                      href="https://t.me/BotFather"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      @BotFather
                    </a>{" "}
                    et tapez <code>/newbot</code>
                  </li>
                  <li>Donnez un nom (ex: Grafton Notifications). BotFather vous renvoie un <strong>token</strong> (collez ci-dessus).</li>
                  <li>
                    Ouvrez ensuite{" "}
                    <a
                      href="https://t.me/userinfobot"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      @userinfobot
                    </a>{" "}
                    et tapez <code>/start</code>. Il vous renvoie votre <strong>chat ID</strong> (collez ci-dessus).
                  </li>
                  <li>
                    Démarrez votre nouveau bot une fois (tapez <code>/start</code> dessus) — obligatoire pour qu'il
                    puisse vous écrire.
                  </li>
                </ol>
              </details>
            </div>
          )}
        </div>

        {/* Thresholds */}
        <div className="border-t border-border/40 pt-5">
          <div className="text-sm font-medium mb-2">Critères "visiteur chaud"</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="min-dur">Durée minimum (secondes)</Label>
              <Input
                id="min-dur"
                type="number"
                value={s.min_duration_seconds}
                onChange={(e) => setS({ ...s, min_duration_seconds: parseInt(e.target.value || "30") })}
                data-testid="notif-min-duration"
              />
            </div>
            <div>
              <Label htmlFor="min-pages">Pages minimum visitées</Label>
              <Input
                id="min-pages"
                type="number"
                value={s.min_pages}
                onChange={(e) => setS({ ...s, min_pages: parseInt(e.target.value || "1") })}
                data-testid="notif-min-pages"
              />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Vous serez notifié uniquement pour les visiteurs qui dépassent ces seuils (anti-spam).
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button onClick={save} disabled={saving} data-testid="notif-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Enregistrer
          </Button>
          <Button variant="outline" onClick={test} disabled={testing} data-testid="notif-test">
            {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-1.5" />}
            Tester maintenant
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
